import { useId, useRef, useState, useLayoutEffect, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * InlinePopover — click-to-open, portal'd, viewport-clamped popover for
 * small inline editors (sliders, selects, number inputs).
 *
 * Mirrors InfoTip's positioning math (clamp bubble to [8, viewportW - w - 8])
 * so the popover can NEVER overflow the viewport on phones down to 320px.
 *
 * Differences vs InfoTip:
 *   - opens on CLICK instead of hover; dismisses on click-outside / Esc
 *   - bubble is `pointer-events: auto` (vs InfoTip's `none`)
 *   - `render` prop returns the editor JSX + receives a `close()` helper
 *     so the caller can commit + close in one callback
 */
export default function InlinePopover({
  children,
  render,
  className = '',
  maxWidth = 260,
  testId,
  ariaLabel,
  title,
  bubbleClassName = 'border-border bg-popover text-popover-foreground',
}) {
  const wrapperRef = useRef(null);
  const bubbleRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ left: 0, top: 0, flipUp: false, width: maxWidth, maxHeight: 320 });
  const id = useId();

  const updatePosition = useCallback(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();

    // Round 92o++++++++N++++++++++ (May 2026) — Modal-aware boundary.
    // When the trigger sits inside a Radix Dialog (or any element
    // with `role="dialog"` / `[data-popover-boundary]`), clamp the
    // popover INSIDE that container instead of the full viewport.
    // User report: the AbbrTip in NetCapitalGainModal was escaping
    // the modal's edges, which is unusable on mobile because the
    // modal's own scroll container can't scroll the floating bubble.
    const boundary = el.closest('[data-popover-boundary], [role="dialog"]');
    const bRect = boundary ? boundary.getBoundingClientRect() : null;
    const padding = 8;
    const minTop = bRect ? bRect.top + padding : padding;
    const maxBottom = bRect ? bRect.bottom - padding : window.innerHeight - padding;
    const minLeft = bRect ? bRect.left + padding : padding;
    const maxRight = bRect ? bRect.right - padding : window.innerWidth - padding;
    const availW = maxRight - minLeft;

    const w = Math.min(maxWidth, Math.max(160, availW));
    const cx = rect.left + rect.width / 2;
    let left = Math.round(cx - w / 2);
    if (left < minLeft) left = minLeft;
    if (left + w > maxRight) left = maxRight - w;

    const belowSpace = maxBottom - rect.bottom - padding;
    const aboveSpace = rect.top - minTop - padding;
    // Flip up when there's more headroom above the trigger than
    // below AND below has less than 180px (rough min content height
    // of a definition popover). Then cap maxHeight so the bubble
    // scrolls internally instead of overflowing the boundary.
    const flipUp = belowSpace < 180 && aboveSpace > belowSpace;
    const top = flipUp ? rect.top - padding : rect.bottom + padding;
    const rawMax = flipUp ? aboveSpace : belowSpace;
    // Floor at 120 so a definition is always at least partially
    // visible — internal scroll handles the rest.
    const maxHeight = Math.max(120, rawMax);
    setPos({ left, top, flipUp, width: w, maxHeight });
  }, [maxWidth]);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    const handler = () => updatePosition();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
  }, [open, updatePosition]);

  // Click-outside + Esc close.
  useEffect(() => {
    if (!open) return;
    const onDocMouse = (e) => {
      if (
        wrapperRef.current?.contains(e.target) ||
        bubbleRef.current?.contains(e.target)
      ) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocMouse);
    document.addEventListener('touchstart', onDocMouse);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouse);
      document.removeEventListener('touchstart', onDocMouse);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = useCallback((e) => {
    e.stopPropagation();
    setOpen((v) => !v);
  }, []);
  const close = useCallback(() => setOpen(false), []);

  return (
    <>
      <button
        ref={wrapperRef}
        type="button"
        onClick={toggle}
        className={className}
        data-testid={testId}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        aria-label={ariaLabel}
        title={title}
      >
        {children}
      </button>
      {open && createPortal(
        <div
          ref={bubbleRef}
          id={id}
          role="dialog"
          data-testid={testId ? `${testId}-bubble` : undefined}
          className={`fixed z-[200] rounded-lg shadow-xl border p-3 overflow-y-auto overscroll-contain ${bubbleClassName}`}
          style={{
            left: pos.left,
            top: pos.flipUp ? undefined : pos.top,
            bottom: pos.flipUp ? (window.innerHeight - pos.top) : undefined,
            width: pos.width,
            maxWidth: 'calc(100vw - 16px)',
            maxHeight: pos.maxHeight,
          }}
          // Round 92o++++++++N++++++++++ (May 2026) — Radix Dialog
          // outside-click guard. The popover bubble is portal'd to
          // document.body (sibling of DialogContent, not a descendant)
          // so any Dialog hosting an AbbrTip needs to opt out of
          // closing when the user clicks INSIDE the popover bubble.
          // We tag the bubble with `data-inline-popover-bubble` and
          // the host Dialog must call `e.preventDefault()` in its
          // `onInteractOutside` / `onPointerDownOutside` when the
          // event originates from a tagged bubble. Without that,
          // Radix's native document pointerdown listener fires before
          // any React synthetic-event stopPropagation can block it.
          data-inline-popover-bubble
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        >
          {render({ close })}
        </div>,
        document.body,
      )}
    </>
  );
}
